import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { FiCheck } from "react-icons/fi";
import { Action, Select, request } from "../shared";
import { StateBlock, Skeleton } from "../../../components/UI";
import { useAsync } from "../../../hooks/useAsync";
import { useLanguage } from "../../../contexts/LanguageContext";
import ItemImage from "./ItemImage";
import { FilterChip, ResetButton, SearchBox } from "./parts";
import {
  Item,
  MODELS,
  PAGE_SIZE,
  decorationName,
  modelQuery,
  rarityKey,
  splitName,
} from "./data";

export interface CatalogFilters {
  q: string;
  model: string;
  offset: number;
}
export const EMPTY_FILTERS: CatalogFilters = { q: "", model: "", offset: 0 };

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  min-width: 0;
`;
const Toolbar = styled.div`
  flex: none;
  display: grid;
  gap: 10px;
  padding: 2px 0 10px;
  grid-template-columns: minmax(0, 1fr);
  @media (min-width: 640px) {
    grid-template-columns: minmax(0, 220px) minmax(0, 1fr);
  }
  select {
    min-height: 44px;
    padding-block: 9px;
    font-size: 15px;
  }
  @media (max-width: 600px) {
    gap: 8px;
    padding-bottom: 6px;
  }
`;
const Meta = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 34px;
  padding-bottom: 6px;
  font-size: 13px;
  color: ${(p) => p.theme.colors.textMuted};
  .count {
    flex: none;
  }
  .chips {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    flex: 1 1 auto;
    overflow: hidden;
  }
  .reset {
    margin-left: auto;
  }
  @media (max-width: 600px) {
    min-height: 30px;
    padding-bottom: 4px;
  }
`;
const Scroll = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 2px 4px 14px 2px;
  margin-right: -4px;
  scrollbar-gutter: stable;
`;
const Grid = styled.ul<{ $dense?: boolean }>`
  list-style: none;
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(
    auto-fill,
    minmax(${(p) => (p.$dense ? "136px" : "200px")}, 1fr)
  );
  @media (max-width: 600px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }
`;
const Card = styled.button<{ $selected: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-rows: auto 1fr;
  gap: 8px;
  padding: 8px 8px 12px;
  text-align: left;
  border-radius: 12px;
  background: ${(p) => p.theme.colors.raised};
  box-shadow: ${(p) =>
    p.$selected
      ? `inset 0 0 0 2px ${p.theme.colors.primary}`
      : `inset 0 0 0 1px ${p.theme.colors.border}`};
  transition:
    box-shadow ${(p) => p.theme.motion.fast},
    background ${(p) => p.theme.motion.fast};
  .img {
    aspect-ratio: 4 / 3;
    background:
      linear-gradient(to top, color-mix(in srgb, var(--rarity, #65778d) 16%, transparent), transparent 55%),
      radial-gradient(
        120% 90% at 50% 25%,
        ${(p) => p.theme.colors.surfaceHover},
        ${(p) => p.theme.colors.navigation}
      );
    box-shadow: inset 0 -3px 0 var(--rarity, #65778d);
    border-radius: 10px;
    overflow: hidden;
    img {
      padding: 10px;
      filter: drop-shadow(0 8px 10px #0007);
      transition: transform ${(p) => p.theme.motion.base}
        ${(p) => p.theme.motion.ease};
    }
  }
  &:hover {
    background: ${(p) => p.theme.colors.surfaceHover};
    box-shadow: ${(p) =>
      p.$selected
        ? `inset 0 0 0 2px ${p.theme.colors.primary}`
        : `inset 0 0 0 1px ${p.theme.colors.borderControl}`};
    .img img {
      transform: scale(1.04);
    }
  }
  .txt {
    display: grid;
    align-content: start;
    gap: 2px;
    padding: 0 4px;
    min-width: 0;
  }
  .model {
    font-size: 12px;
    color: ${(p) => p.theme.colors.textMuted};
    overflow-wrap: anywhere;
  }
  .skin {
    font-size: 14px;
    font-weight: 600;
    line-height: 1.3;
    overflow-wrap: anywhere;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .rarity {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 2px;
    font-size: 12px;
    color: ${(p) => p.theme.colors.textSecondary};
    &::before {
      content: "";
      width: 12px;
      height: 3px;
      border-radius: 2px;
      background: var(--rarity, #65778d);
    }
  }
  .tick {
    position: absolute;
    top: 14px;
    right: 14px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px 2px 6px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    background: ${(p) => p.theme.colors.primary};
    color: ${(p) => p.theme.colors.onPrimary};
    svg {
      width: 12px;
      height: 12px;
    }
  }
`;
const Pager = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 0 0;
  border-top: 1px solid ${(p) => p.theme.colors.border};
  color: ${(p) => p.theme.colors.textMuted};
  font-size: 14px;
  button {
    min-height: 40px;
    padding-block: 7px;
  }
`;

interface Props {
  kind: string;
  filters: CatalogFilters;
  onFilters: (f: CatalogFilters) => void;
  onPick: (item: Item) => void;
  selectedId?: string;
  /** Показывать выбор модели (только для оружия, ножей и перчаток). */
  withModels?: boolean;
  searchLabel: string;
  autoFocus?: boolean;
  dense?: boolean;
}

/**
 * Каталог: фильтры и пагинация закреплены в своих областях, прокручивается только сетка.
 * Родитель должен задать высоту (grid/flex с min-height: 0).
 */
export default function CatalogPanel({
  kind,
  filters,
  onFilters,
  onPick,
  selectedId,
  withModels,
  searchLabel,
  autoFocus,
  dense,
}: Props) {
  const { t } = useLanguage();
  const [debounced, setDebounced] = useState(filters.q);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastTotal = useRef(0);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(filters.q), 250);
    return () => clearTimeout(id);
  }, [filters.q]);

  // Выбранная модель добавляется к поиску как «Модель |» (поиск — по подстроке).
  const query = [modelQuery(filters.model), debounced.trim()]
    .filter(Boolean)
    .join(" ");
  const key = `${kind}|${query}|${filters.offset}`;
  const state = useAsync<{ items: Item[]; total: number }>(key, (signal) =>
    request(
      "/skins/catalog?kind=" +
        encodeURIComponent(kind) +
        "&q=" +
        encodeURIComponent(query) +
        "&offset=" +
        filters.offset,
      undefined,
      undefined,
      signal,
    ),
  );
  if (state.status === "success" && state.data) lastTotal.current = state.data.total;

  // Новая страница или новый запрос: сетка начинается сверху.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [key]);

  const models = withModels ? MODELS[kind] || [] : [];
  const hasFilters = !!filters.q || !!filters.model;
  const total = state.status === "success" && state.data ? state.data.total : lastTotal.current;
  const from = state.data && state.data.items.length ? filters.offset + 1 : 0;
  const to = state.data ? filters.offset + state.data.items.length : 0;
  const patch = (p: Partial<CatalogFilters>) =>
    onFilters({ ...filters, offset: 0, ...p });
  const loading = state.status === "loading";

  return (
    <Wrap>
      <Toolbar>
        {models.length > 0 && (
          <Select
            aria-label={t("skins.model")}
            value={filters.model}
            onChange={(e) => patch({ model: e.target.value })}
          >
            <option value="">{t("skins.all_models")}</option>
            {models.map((m) => (
              <option key={m} value={m}>
                {m.replace(/^★\s*/, "")}
              </option>
            ))}
          </Select>
        )}
        <div style={{ gridColumn: models.length ? undefined : "1 / -1" }}>
          <SearchBox
            autoFocus={autoFocus}
            label={searchLabel}
            value={filters.q}
            onChange={(q) => patch({ q })}
          />
        </div>
      </Toolbar>

      {hasFilters && (
        <Meta>
          <span className="chips" aria-label={t("skins.active_filters")}>
            {filters.model && (
              <FilterChip
                removeLabel={t("skins.remove_filter", { value: filters.model.replace(/^★\s*/, "") })}
                onRemove={() => patch({ model: "" })}
              >
                {filters.model.replace(/^★\s*/, "")}
              </FilterChip>
            )}
            {filters.q && (
              <FilterChip
                removeLabel={t("skins.remove_filter", { value: filters.q })}
                onRemove={() => patch({ q: "" })}
              >
                “{filters.q}”
              </FilterChip>
            )}
          </span>
          <span className="reset">
            <ResetButton label={t("skins.reset_all")} onClick={() => onFilters(EMPTY_FILTERS)} />
          </span>
        </Meta>
      )}

      <Scroll ref={scrollRef} tabIndex={-1}>
        {state.status === "error" ? (
          <StateBlock
            kind="error"
            title={t("skins.catalog_error")}
            text={state.error}
            onAction={state.reload}
          />
        ) : state.status !== "success" || !state.data ? (
          <Grid $dense={dense} role="status" aria-busy="true" aria-label={t("common.loading")}>
            {Array.from({ length: 10 }).map((_, i) => (
              <li key={i}>
                <Skeleton $h={dense ? 176 : 214} $r={12} />
              </li>
            ))}
          </Grid>
        ) : state.data.items.length === 0 ? (
          <StateBlock
            kind="empty"
            title={t("skins.nothing_found")}
            text={
              filters.q
                ? t("skins.nothing_found_for", { value: filters.q })
                : undefined
            }
            actionLabel={hasFilters ? t("skins.reset_all") : undefined}
            onAction={hasFilters ? () => onFilters(EMPTY_FILTERS) : undefined}
          />
        ) : (
          <Grid $dense={dense}>
            {state.data.items.map((item) => {
              const { model, skin } = splitName(
                kind === "sticker" || kind === "charm"
                  ? decorationName(item)
                  : item.name,
              );
              const rk = rarityKey(item);
              const selected = item.id === selectedId;
              return (
                <li key={item.id}>
                  <Card
                    type="button"
                    $selected={selected}
                    aria-pressed={selected}
                    title={item.name}
                    style={{ "--rarity": item.color } as React.CSSProperties}
                    onClick={() => onPick(item)}
                  >
                    <span className="img">
                      <ItemImage src={item.image} alt="" />
                    </span>
                    <span className="txt">
                      {skin ? (
                        <>
                          <span className="model">{model}</span>
                          <span className="skin">{skin}</span>
                        </>
                      ) : (
                        <span className="skin">{model}</span>
                      )}
                      {rk && <span className="rarity">{t(rk)}</span>}
                    </span>
                    {selected && (
                      <span className="tick">
                        <FiCheck aria-hidden="true" />
                        {t("skins.installed")}
                      </span>
                    )}
                  </Card>
                </li>
              );
            })}
          </Grid>
        )}
      </Scroll>

      <Pager>
        <span aria-live="polite">
          {state.status === "success" && total > 0 ? t("skins.range", { from, to, total }) : "\u00a0"}
        </span>
        <span style={{ display: "flex", gap: 8 }}>
          <Action
            type="button"
            disabled={filters.offset === 0 || loading}
            onClick={() =>
              onFilters({ ...filters, offset: Math.max(0, filters.offset - PAGE_SIZE) })
            }
          >
            {t("common.back")}
          </Action>
          <Action
            type="button"
            disabled={filters.offset + PAGE_SIZE >= total || loading}
            onClick={() => onFilters({ ...filters, offset: filters.offset + PAGE_SIZE })}
          >
            {t("common.next")}
          </Action>
        </span>
      </Pager>
    </Wrap>
  );
}
